import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import CleanHeading from '../../../components/ui/HolographicText';

const FeaturedTransformation = () => {
  const featuredStory = {
    client: "Global Manufacturing Leader",
    industry: "Manufacturing & Operations",
    challenge: "Legacy systems causing 40% operational inefficiency and $2M annual losses",
    solution: "End-to-end digital transformation with AI-powered process optimization",
    timeline: "8 months",
    results: [
      { metric: "Operational Efficiency", before: "60%", after: "95%", improvement: "+35%" },
      { metric: "Cost Savings", before: "$2M Loss", after: "$3M Profit", improvement: "$5M Impact" },
      { metric: "Processing Time", before: "48 hours", after: "4 hours", improvement: "92% Faster" },
      { metric: "Error Rate", before: "12%", after: "0.8%", improvement: "93% Reduction" }
    ],
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop",
    testimonial: "Ons Mettle didn\'t just implement technology—they transformed our entire operational mindset. The results exceeded our most optimistic projections.",
    clientName: "Sarah Chen",
    clientTitle: "Chief Operations Officer"
  };



  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <CleanHeading
            size="text-3xl sm:text-4xl lg:text-5xl"
            variant="primary"
            className="mb-6"
          >
            Featured Success Story
          </CleanHeading>
          <p className="text-xl text-text-secondary font-body">
            Discover how we turned operational challenges into{' '}
            <span className="text-telecom-secondary font-semibold">competitive advantages</span>
          </p>
        </motion.div>

        <div className="bg-white rounded-lg shadow-lg border border-border overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Image Section */}
            <div className="relative h-64 lg:h-auto overflow-hidden">
              <Image
                src={featuredStory.image}
                alt="Manufacturing transformation project"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <p className="text-sm font-medium font-body">{featuredStory.industry}</p>
                <h3 className="text-xl font-bold font-heading">{featuredStory.client}</h3>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8 lg:p-12">
              <div className="space-y-8">
                {/* Challenge & Solution */}
                <div>
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center font-heading">
                      <Icon name="AlertTriangle" size={20} className="text-telecom-accent mr-2" />
                      The Challenge
                    </h4>
                    <p className="text-text-secondary font-body">{featuredStory.challenge}</p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center font-heading">
                      <Icon name="Lightbulb" size={20} className="text-brand-primary mr-2" />
                      Our Solution
                    </h4>
                    <p className="text-text-secondary font-body">{featuredStory.solution}</p>
                  </div>
                </div>

                {/* Results Grid */}
                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center font-heading">
                    <Icon name="TrendingUp" size={20} className="text-telecom-secondary mr-2" />
                    Measurable Results
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {featuredStory.results.map((result, index) => (
                      <div key={index} className="bg-muted p-4 rounded-lg border border-border">
                        <p className="text-sm text-text-muted mb-1 font-body">{result.metric}</p>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-destructive font-body">{result.before}</span>
                          <Icon name="ArrowRight" size={16} className="text-text-muted" />
                          <span className="text-sm text-telecom-secondary font-semibold font-body">{result.after}</span>
                        </div>
                        <p className="text-lg font-bold text-brand-primary font-heading">{result.improvement}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Testimonial */}
                <div className="bg-muted p-6 rounded-lg border-l-4 border-telecom-accent">
                  <blockquote className="text-foreground italic mb-4">
                    "{featuredStory.testimonial}"
                  </blockquote>
                  <div className="flex items-center">
                    <div>
                      <p className="font-semibold text-telecom-accent">{featuredStory.clientName}</p>
                      <p className="text-sm text-text-secondary">{featuredStory.clientTitle}</p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/featured-projects" className="flex-1">
                    <Button
                      variant="default"
                      fullWidth
                      iconName="FolderOpen"
                      iconPosition="left"
                    >
                      View All Case Studies
                    </Button>
                  </Link>
                  <Link to="/contact-engagement" className="flex-1">
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="MessageCircle"
                      iconPosition="left"
                    >
                      Discuss Your Project
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedTransformation;